import { ChevronRight, Close, ExpandMore, FilterListRounded } from '@mui/icons-material';
import { Box, IconButton, InputAdornment, TextField } from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import React, { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface MenuTreeNodeVOWithPath {
  path?: string;
  displayName?: string;
  children?: MenuTreeNodeVOWithPath[];
  id?: string;
}
export const MENU_CONFIG = [
  {
    id: "1",
    path: "/menu-manager",
    displayName: "Menu Manager",
  },
  {
    id: "2",
    path: "/page-manager",
    displayName: "Page Manager",
  },
  {
    id: "3",
    path: "/view-manager",
    displayName: "View Manager",
  },

]

const SideMenu: React.FC = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedItemId, setSelectedItemId] = React.useState<string | null>(null);

  useEffect(() => {
    const currentPath = location.pathname;
    for (const item of MENU_CONFIG) {
      if (item.path === currentPath) {
        setSelectedItemId(item.id);
      }
    }
  }, [location.pathname]);

  const handleMenuItemClick = useCallback((item: MenuTreeNodeVOWithPath) => {
    const pathString = item.path;
    if (!pathString || item.children?.length) return;
    if (selectedItemId === item.id) return;
    navigate(pathString);
  }, [navigate, selectedItemId]);

  const renderTree = useCallback((items: MenuTreeNodeVOWithPath[]) => {
    return items.map((item) => (
      <TreeItem
        key={item.id as string}
        itemId={item.id as string}
        data-testid={`side-menu-item-${item.id}`}
        label={item.displayName}
        onClick={(event) => {
          event.stopPropagation();
          handleMenuItemClick(item)
        }}
        sx={{
          '& .MuiTreeItem-iconContainer': {
            width: 0,
          },
          '&': {
            marginBottom: '10px',
          },
          '& .MuiTreeItem-content[data-selected]': {
            backgroundColor: 'transparent',
            color: '#295ef1',
          }
        }}
      >
        {item.children ? renderTree(item.children) : null}
      </TreeItem>
    ));
  }, [handleMenuItemClick]);

  const filterMenuItems = (items: MenuTreeNodeVOWithPath[], search: string): MenuTreeNodeVOWithPath[] => {
    if (!search.trim()) return items;

    return items.filter(item => {
      const currentMatches = item?.displayName?.toLowerCase().includes(search.toLowerCase());
      return currentMatches;
    });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const filteredMenuData = filterMenuItems(MENU_CONFIG, searchTerm);
  return (
    <Box className="side-menu" sx={{ width: '100%', pt: '6px' }}>
      <TextField
        fullWidth
        variant="outlined"
        value={searchTerm}
        onChange={handleSearchChange}
        size="small"
        sx={{ mb: 2 }}
        autoComplete="off"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <FilterListRounded color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Clear search"
                  onClick={handleClearSearch}
                  edge="end"
                  size="small"
                >
                  <Close />
                </IconButton>
              </InputAdornment>
            ),
          }
        }}
      />
      <SimpleTreeView
        selectedItems={selectedItemId}
        slots={{
          collapseIcon: ExpandMore,
          expandIcon: ChevronRight,
        }}
      >
        {renderTree(filteredMenuData)}
      </SimpleTreeView>
    </Box>
  );
};

export default SideMenu;